import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Checkbox,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { normalizeColOptions } from "app/websocketManager/utils";
import GFFormFieldRenderer from "./GFFormFieldRenderer";
import GFSubsheetForm from "./GFSubsheetForm";
import { GF_FORM_SECTIONS, GF_FULL_WIDTH_COLUMN_IDS, GF_FORMULA_COLUMN_IDS } from "./GFFormSections";
import { getColumnLabel, getSectionTitle, getEnglishColumnLabel } from "./GFTranslations";
import { parseFiles } from "../../views/table/columnEditors/ReusableColumnEditorsFactory/ColumnEditorFactory";
import ApiClient from "utils/ApiClient";

const CONSENT_LANGUAGE_COL_ID = "1128";
const CONSENT_SUBSHEET_COL_ID = "1132";
const CASE_SOURCE_COL_ID = "1141";

const CASE_SOURCE_CONDITIONAL_COLS = {
  "1142": "Other",
  "1143": "Screening Camp GF Supported",
  "1144": "Screening Camp Hospital Supported",
  "1145": "Out Reach OPD",
  "1146": "Hospital Referred By",
  "1147": "Hospital Referred By",
};

const EDUCATION_STATUS_COL_ID = "1156";
const HIGHER_EDUCATION_COL_ID = "1285";

const CHD_CASE_COL_ID      = "1211";
const CHD_COMMENTS_COL_ID  = "1296";

const ID_PROOF_CONDITIONAL_COLS = {
  "1289": { triggerColId: "1286", triggerValue: "Aadhar Card" },
  "1290": { triggerColId: "1286", triggerValue: "Voter ID Card" },
  "1291": { triggerColId: "1287", triggerValue: "Aadhar Card" },
  "1292": { triggerColId: "1287", triggerValue: "Voter ID Card" },
  "1293": { triggerColId: "1288", triggerValue: "Aadhar Card" },
  "1294": { triggerColId: "1288", triggerValue: "Voter ID Card" },
};

const SECTION_COLOR = "#f59e0b";

function buildInitialFormData(columns) {
  const fd = {
    row_id: "",
    isModified: false,
  };
  columns.forEach((col) => {
    const typeId = String(col.column_type_id ?? "1");
    const key = `column_${col.id}`;
    if (typeId === "12") fd[key] = "0";
    else if (typeId === "9") fd[key] = "[]";
    else fd[key] = "";
  });
  return fd;
}

function renderDeclarationTemplate(template, nameElement, designationElement) {
  if (!template) return null;
  const parts = [];
  let currentText = template;
  while (true) {
    const idxName = currentText.indexOf("{name}");
    const idxDes = currentText.indexOf("{designation}");

    if (idxName === -1 && idxDes === -1) {
      parts.push(currentText);
      break;
    }

    if (idxName !== -1 && (idxDes === -1 || idxName < idxDes)) {
      parts.push(currentText.substring(0, idxName));
      parts.push(nameElement);
      currentText = currentText.substring(idxName + 6);
    } else {
      parts.push(currentText.substring(0, idxDes));
      parts.push(designationElement);
      currentText = currentText.substring(idxDes + 13);
    }
  }
  return <>{parts}</>;
}

function isMandatoryCol(column) {
  const opts = normalizeColOptions(column?.options);
  return opts?.is_mandatory === true || opts?.is_mandatory === "1" || opts?.is_mandatory === 1;
}

function isColumnVisible(colId, caseSourceValue, formData) {
  const id = String(colId);

  const caseSourceTrigger = CASE_SOURCE_CONDITIONAL_COLS[id];
  if (caseSourceTrigger) {
    if (!caseSourceValue) return false;
    return String(caseSourceValue).trim().toLowerCase() === caseSourceTrigger.toLowerCase();
  }

  if (id === HIGHER_EDUCATION_COL_ID) {
    const eduVal = formData ? formData[`column_${EDUCATION_STATUS_COL_ID}`] : "";
    return String(eduVal ?? "").trim().toLowerCase() === "higher education";
  }

  if (id === CHD_COMMENTS_COL_ID) {
    const chdVal = formData ? formData[`column_${CHD_CASE_COL_ID}`] : "";
    return String(chdVal ?? "").trim().toLowerCase() === "no";
  }

  const idProofRule = ID_PROOF_CONDITIONAL_COLS[id];
  if (idProofRule) {
    const triggerVal = formData ? formData[`column_${idProofRule.triggerColId}`] : "";
    return String(triggerVal ?? "").trim().toLowerCase() === idProofRule.triggerValue.toLowerCase();
  }

  return true;
}

function validateForm(columns, formData, caseSourceValue) {
  const errors = {};
  columns.forEach((col) => {
    const colId = String(col.id);
    if (!isColumnVisible(colId, caseSourceValue, formData)) return;
    const isConditionalMandatory = !!CASE_SOURCE_CONDITIONAL_COLS[colId] || !!ID_PROOF_CONDITIONAL_COLS[colId];
    if (!isMandatoryCol(col) && !isConditionalMandatory) return;
    const typeId = String(col.column_type_id ?? "1");
    if (GF_FORMULA_COLUMN_IDS.has(colId)) return;
    const val = formData[`column_${col.id}`];
    let isEmpty = false;
    if (typeId === "12") {
      isEmpty = false;
    } else if (typeId === "9") {
      const files = parseFiles(val);
      isEmpty = !files || files.length === 0;
    } else if (typeId === "10" || typeId === "10-1") {
      isEmpty = !val || !(val?.value || (typeof val === "string" && val.trim()));
    } else if (typeId === "6") {
      const subRows = val?.value ?? (Array.isArray(val) ? val : []);
      isEmpty = !subRows || subRows.length === 0;
    } else if (Array.isArray(val)) {
      isEmpty = val.length === 0;
    } else if (typeof val === "string") {
      isEmpty = val.trim() === "";
    } else {
      isEmpty = val === "" || val === null || val === undefined;
    }
    if (isEmpty) errors[colId] = "This field is required";
  });

  const declVal = formData["column_1281"];
  const isDeclChecked = declVal === "1" || declVal === true || declVal === 1;
  if (!isDeclChecked) {
    errors["1281"] = "You must accept the declaration to submit.";
  } else {
    const nameVal = formData["column_1279"];
    const desVal = formData["column_1280"];
    if (!nameVal || !nameVal.trim()) {
      errors["1279"] = "This field is required";
    }
    if (!desVal || !desVal.trim()) {
      errors["1280"] = "This field is required";
    }
  }

  return errors;
}

function buildGFPayload(formColumns, formData) {
  const payload = {};

  formColumns.forEach((col) => {
    const colId = String(col.id);
    const typeId = String(col.column_type_id ?? "1");
    const opts = normalizeColOptions(col.options);

    if (opts?.is_read_only || opts?.is_star_column) return;
    if (typeId === "7" || typeId === "19") return;
    if (col.is_default == 1 || col.default_type == 1 || col.alias_name === "createdBy") return;

    if (typeId === "6") {
      const subsheetVal = formData[`column_${colId}`];
      if (subsheetVal && typeof subsheetVal === "object" && Array.isArray(subsheetVal.value)) {
        payload[colId] = {
          value: subsheetVal.value,
          column_type_id: typeId,
          row_id: "",
          sub_list_id: subsheetVal.sub_list_id ?? opts?.sub_list_id,
          is_modified: true,
        };
      }
      return;
    }

    let value = formData[`column_${colId}`] ?? "";

    if (typeId === "10") {
      if (!value) value = { reference_column_id: null, value: "" };
      value = typeof value === "string" ? value : JSON.stringify(value);
    } else if (typeId === "9") {
      if (!value) value = [];
      value = typeof value === "string" ? value : JSON.stringify(value);
    }

    if ((value === "" || value === null || value === undefined) && typeId === "12") value = false;
    else if ((value === "" || value === null || value === undefined)) value = value ?? "";

    const isModified = !GF_FORMULA_COLUMN_IDS.has(colId);

    payload[colId] = {
      value,
      column_type_id: typeId,
      row_id: "",
      is_modified: isModified,
    };
  });

  return payload;
}

function GFApplicationForm({ store, onClose }) {
  const allListColumns = store?.listConfig?.listData ?? [];
  const columnById = {};
  allListColumns.forEach((col) => {
    columnById[String(col.id)] = {
      ...col,
      normalizedColumnOptions: normalizeColOptions(col?.options),
    };
  });

  const sectionColumnIds = GF_FORM_SECTIONS.flatMap((s) => s.columnIds);
  const formColumns = sectionColumnIds.map((id) => columnById[id]).filter(Boolean);

  const subsheetRawColumn = (store?.listConfig?.listData ?? []).find(
    (col) => String(col.id) === CONSENT_SUBSHEET_COL_ID
  );
  const subColumns = subsheetRawColumn?.list_columns ?? [];
  const subsheetColumn = columnById[CONSENT_SUBSHEET_COL_ID];

  const [formData, setFormData] = useState(() => buildInitialFormData(formColumns));
  const [activeLanguage, setActiveLanguage] = useState("");
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [formData.isModified]);

  const handleFieldChange = useCallback((columnId, newValue) => {
    const strId = String(columnId);
    setFormData((prev) => ({
      ...prev,
      [`column_${columnId}`]: newValue,
      isModified: true,
    }));
    setErrors((prev) => {
      if (!prev[strId] && !prev[columnId]) return prev;
      const next = { ...prev };
      delete next[strId];
      delete next[columnId];
      return next;
    });
    if (strId === CONSENT_LANGUAGE_COL_ID) {
      setActiveLanguage(newValue || "");
    }
  }, []);
  console.log("formData", formData)

  const handleSubsheetChange = useCallback((formattedSubsheet) => {
    setFormData((prev) => ({
      ...prev,
      [`column_${CONSENT_SUBSHEET_COL_ID}`]: formattedSubsheet,
      [`column_${CONSENT_SUBSHEET_COL_ID}_isModified`]: true,
      isModified: true,
    }));
    setErrors((prev) => { const n = { ...prev }; delete n[CONSENT_SUBSHEET_COL_ID]; return n; });
  }, []);

  const handleSubmit = async () => {
    const caseSourceValue = formData[`column_${CASE_SOURCE_COL_ID}`] || "";
    const validationErrors = validateForm(formColumns, formData, caseSourceValue);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrId = Object.keys(validationErrors)[0];
      const el = document.getElementById(`gf-field-${firstErrId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildGFPayload(formColumns, formData);
      console.log("GF Form payload:", payload);
      const viewStore = store?.currentViewStore ?? store;
      const listId = viewStore?.parentListId ?? viewStore?.listId;
      const resp = await ApiClient.List.saveRowForm({
        list_id: listId,
        data: JSON.stringify(payload),
      });
      if (resp) {
        window?.appStore?.notificationStore?.success?.("Application submitted successfully!");
        const gridApi = store?.gridApi || store?.currentViewStore?.gridApi;
        if (gridApi) {
          try { viewStore?.refreshData?.(); } catch (_) {}
        }
        onClose();
      }
    } catch (err) {
      console.error("GF Form submit error:", err);
      const errMsg = err?.response?.errors?.[0] || err?.message || "Something went wrong. Please try again.";
      window?.appStore?.notificationStore?.error?.(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (formData.isModified) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to close?");
      if (!confirmed) return;
    }
    onClose();
  };

  const renderHospitalDeclaration = (sub, sectionColor) => {
    const currentName = formData["column_1279"] || "";
    const currentDesignation = formData["column_1280"] || "";
    const isChecked = formData["column_1281"] === "1" || formData["column_1281"] === true || formData["column_1281"] === 1;

    const checkboxEl = (
      <Checkbox
        checked={isChecked}
        onChange={(e) => handleFieldChange("1281", e.target.checked ? "1" : "0")}
        color="primary"
        style={{ color: "#0f766e", padding: 4 }}
      />
    );

    const nameInput = (
      <TextField
        id="gf-field-1279"
        value={currentName}
        onChange={(e) => handleFieldChange("1279", e.target.value)}
        placeholder="name of person on behalf of hospital"
        error={!!errors["1279"]}
        variant="standard"
        style={{
          display: "inline-flex",
          margin: "0 6px",
          minWidth: 260,
          verticalAlign: "baseline",
        }}
        inputProps={{
          style: {
            fontSize: "13px",
            fontWeight: 700,
            textAlign: "center",
            padding: "2px 0",
            color: "#0f766e",
          },
        }}
      />
    );

    const designationInput = (
      <TextField
        id="gf-field-1280"
        value={currentDesignation}
        onChange={(e) => handleFieldChange("1280", e.target.value)}
        placeholder="designation"
        error={!!errors["1280"]}
        variant="standard"
        style={{
          display: "inline-flex",
          margin: "0 6px",
          minWidth: 160,
          verticalAlign: "baseline",
        }}
        inputProps={{
          style: {
            fontSize: "13px",
            fontWeight: 700,
            textAlign: "center",
            padding: "2px 0",
            color: "#0f766e",
          },
        }}
      />
    );

    const col1281 = columnById["1281"] || { id: "1281", name: "I, {name}, of designation {designation} have seen each Aadhaar Card/Voter ID and Income proof (inclusive of ones in vernacular languages) mentioned above and also ensure that details match those in the Application Form." };
    const englishTemplate = getEnglishColumnLabel(col1281);
    const activeTemplate = getColumnLabel(col1281, activeLanguage || "English");

    const staticEnglishText = englishTemplate
      .replace("{name}", currentName ? `"${currentName}"` : "_______________________")
      .replace("{designation}", currentDesignation ? `"${currentDesignation}"` : "_______________________");

    const showDualLanguage = activeLanguage && activeLanguage !== "English";

    return (
      <Box
        key={sub.id}
        mb={2}
        id="gf-field-1281"
        style={{ gridColumn: "1 / -1" }}
      >
        <Box
          mb={1.5}
          px={1.5}
          py={0.75}
          style={{
            background: "#ccfbf1",
            borderRadius: 6,
            borderLeft: `3px solid ${sectionColor}`,
          }}
        >
          {activeLanguage && activeLanguage !== "English" && (
            <Typography variant="caption" style={{ color: "#0f766e", opacity: 0.55, display: "block", fontSize: 10, fontWeight: 400, lineHeight: 1.3 }}>
              {sub.title}
            </Typography>
          )}
          <Typography variant="caption" style={{ color: "#0f766e", fontWeight: 700, fontSize: 12 }}>
            {getSectionTitle(sub.id, sub.title, activeLanguage)}
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="flex-start"
          p={2}
          style={{
            background: "#f0fdfa",
            borderRadius: 8,
            border: `1px solid ${errors["1281"] ? "#f44336" : "#99f6e4"}`,
            gap: 12,
          }}
        >
          <Box style={{ flexShrink: 0, marginTop: 2 }}>{checkboxEl}</Box>
          <Box style={{ flexGrow: 1 }}>
            {showDualLanguage && (
              <Typography
                variant="caption"
                style={{
                  display: "block",
                  color: "rgba(0,0,0,0.38)",
                  fontSize: "11px",
                  lineHeight: 1.5,
                  marginBottom: 8,
                  fontStyle: "italic",
                }}
              >
                {staticEnglishText}
              </Typography>
            )}

            <Typography
              variant="body2"
              style={{
                color: "#1f2937",
                fontSize: "13px",
                lineHeight: 2,
              }}
            >
              {renderDeclarationTemplate(activeTemplate, nameInput, designationInput)}
            </Typography>

            {errors["1281"] && (
              <Typography variant="caption" style={{ color: "#f44336", display: "block", marginTop: 8 }}>
                {errors["1281"]}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const caseSourceValue = formData[`column_${CASE_SOURCE_COL_ID}`] || "";
  const isConditionalMandatory = (colId) => !!CASE_SOURCE_CONDITIONAL_COLS[String(colId)];

  const renderField = (column) => {
    const colId = String(column.id);
    const isFullWidth = GF_FULL_WIDTH_COLUMN_IDS.has(colId);
    const isForcedMandatory = isConditionalMandatory(colId);
    const columnWithMandatory = isForcedMandatory
      ? {
          ...column,
          options: {
            ...(column.options ?? {}),
            is_mandatory: "1",
          },
          normalizedColumnOptions: {
            ...(column.normalizedColumnOptions ?? {}),
            is_mandatory: true,
          },
        }
      : column;

    return (
      <Box
        key={colId}
        id={`gf-field-${colId}`}
        style={isFullWidth ? { gridColumn: "1 / -1" } : {}}
      >
        <GFFormFieldRenderer
          column={columnWithMandatory}
          value={formData[`column_${colId}`] ?? ""}
          onChange={(val) => handleFieldChange(colId, val)}
          activeLanguage={activeLanguage}
          error={errors[colId] || null}
          store={store}
          data={formData}
          isFullWidth={isFullWidth}
          subColumns={subColumns}
        />
      </Box>
    );
  };

  const formContent = (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        style: {
          borderRadius: 12,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle disableTypography style={{ padding: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          py={1.5}
          style={{
            background: "#fff",
            borderBottom: "3px solid #f59e0b",
          }}
        >
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Box
              display="flex" alignItems="center" justifyContent="center"
              style={{
                width: 38, height: 38, borderRadius: 8,
                background: "#fef3c7", fontSize: 20,
                border: "1px solid #fde68a",
              }}
            >
              🏥
            </Box>
            <Box>
              <Typography variant="h6" style={{ color: "#92400e", fontWeight: 700, lineHeight: 1.2, fontSize: 16 }}>
                GF Application Form
              </Typography>
              <Typography variant="caption" style={{ color: "#b45309" }}>
                Genesis Foundation — New Patient Application
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            style={{ color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ padding: "20px 24px", background: "#fff" }}>
        {GF_FORM_SECTIONS.map((section, idx) => {
          const sectionColor = section.color ?? "#f59e0b";

          if (section.subSections) {
            const allSectionCols = section.columnIds
              .map((id) => columnById[id])
              .filter(Boolean);
            if (allSectionCols.length === 0) return null;

            const introCols = (section.introColumnIds ?? [])
              .map((id) => columnById[id])
              .filter(Boolean);

            return (
              <Box key={section.id} mb={3}>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{
                    background: "#f0fdfa",
                    borderRadius: "8px 8px 0 0",
                    padding: "8px 16px",
                    gap: 10,
                    borderLeft: `4px solid ${sectionColor}`,
                    borderBottom: `1px solid #99f6e4`,
                  }}
                >
                  <Box
                    display="flex" alignItems="center" justifyContent="center"
                    style={{ width: 22, height: 22, borderRadius: "50%", background: sectionColor, flexShrink: 0 }}
                  >
                    <Typography variant="caption" style={{ color: "#fff", fontWeight: 700 }}>{idx + 1}</Typography>
                  </Box>
                  <Box>
                    {activeLanguage && activeLanguage !== "English" && (
                      <Typography variant="caption" style={{ color: "#0f766e", opacity: 0.55, display: "block", fontSize: 10, fontWeight: 400, lineHeight: 1.3 }}>
                        {section.title}
                      </Typography>
                    )}
                    <Typography variant="subtitle2" style={{ color: "#0f766e", fontWeight: 700, fontSize: 13 }}>
                      {getSectionTitle(section.id, section.title, activeLanguage)}
                    </Typography>
                  </Box>
                </Box>

                <Paper
                  variant="outlined"
                  style={{
                    borderRadius: "0 0 8px 8px",
                    borderTop: "none",
                    borderColor: "#99f6e4",
                    borderLeft: `4px solid ${sectionColor}`,
                  }}
                >
                  <Box p={2}>
                    {section.banner && (
                      <Box
                        mb={2}
                        p={2}
                        style={{
                          background: "#f0fdfa",
                          borderRadius: 8,
                          border: "1px solid #99f6e4",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          style={{ color: "#0f766e", fontWeight: 700, fontSize: 13, marginBottom: 6 }}
                        >
                          {section.banner.title}
                        </Typography>
                        <Typography variant="body2" style={{ color: "#374151", fontSize: 12, lineHeight: 1.6 }}>
                          {section.banner.description}
                        </Typography>
                      </Box>
                    )}

                    {introCols.length > 0 && (
                      <Box
                        mb={2}
                        display="grid"
                        style={{ gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}
                      >
                        {introCols.map((col) => renderField(col))}
                      </Box>
                    )}

                    {section.subSections.map((sub) => {
                      if (sub.id === "verification_hospital") {
                        return renderHospitalDeclaration(sub, sectionColor);
                      }

                      const subCols = sub.columnIds.map((id) => columnById[id]).filter(Boolean);
                      if (subCols.length === 0) return null;
                      return (
                        <Box key={sub.id} mb={2}>
                          <Box
                            mb={1}
                            px={1.5}
                            py={0.75}
                            style={{
                              background: "#ccfbf1",
                              borderRadius: 6,
                              borderLeft: `3px solid ${sectionColor}`,
                            }}
                          >
                            {activeLanguage && activeLanguage !== "English" && (
                              <Typography variant="caption" style={{ color: "#0f766e", opacity: 0.55, display: "block", fontSize: 10, fontWeight: 400, lineHeight: 1.3 }}>
                                {sub.title}
                              </Typography>
                            )}
                            <Typography variant="caption" style={{ color: "#0f766e", fontWeight: 700, fontSize: 12 }}>
                              {getSectionTitle(sub.id, sub.title, activeLanguage)}
                            </Typography>
                          </Box>
                          <Box
                            display="grid"
                            style={{ gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}
                          >
                            {subCols.map((col) => renderField(col))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Box>
            );
          }

          const sectionCols = section.columnIds
            .map((id) => columnById[id])
            .filter(Boolean);
          if (sectionCols.length === 0) return null;

          return (
            <Box key={section.id} mb={3}>
              <Box
                display="flex"
                alignItems="center"
                style={{
                  background: "#fff8e6",
                  borderRadius: "8px 8px 0 0",
                  padding: "8px 16px",
                  gap: 10,
                  borderLeft: "4px solid #f59e0b",
                  borderBottom: "1px solid #fde68a",
                }}
              >
                <Box
                  display="flex" alignItems="center" justifyContent="center"
                  style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: "#f59e0b", flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" style={{ color: "#fff", fontWeight: 700 }}>
                    {idx + 1}
                  </Typography>
                </Box>
                <Box>
                  {activeLanguage && activeLanguage !== "English" && (
                    <Typography variant="caption" style={{ color: "#92400e", opacity: 0.55, display: "block", fontSize: 10, fontWeight: 400, lineHeight: 1.3 }}>
                      {section.title}
                    </Typography>
                  )}
                  <Typography variant="subtitle2" style={{ color: "#92400e", fontWeight: 700, fontSize: 13 }}>
                    {getSectionTitle(section.id, section.title, activeLanguage)}
                  </Typography>
                </Box>
              </Box>

              <Paper
                variant="outlined"
                style={{
                  borderRadius: "0 0 8px 8px",
                  borderTop: "none",
                  borderColor: "#fde68a",
                  borderLeft: "4px solid #f59e0b",
                }}
              >
                <Box
                  p={2}
                  display="grid"
                  style={{ gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}
                >
                  {sectionCols
                    .filter((col) => isColumnVisible(String(col.id), caseSourceValue, formData))
                    .map((col) => renderField(col))}
                </Box>
              </Paper>
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px", background: "#fff", borderTop: "1px solid #fde68a" }}>
        <Button onClick={handleClose} disabled={isSaving} color="default" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSaving}
          variant="contained"
          style={{
            background: isSaving ? undefined : "#f59e0b",
            color: "#fff",
            fontWeight: 700,
            minWidth: 160,
            boxShadow: "none",
          }}
          startIcon={isSaving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
        >
          {isSaving ? "Submitting..." : "Submit Application"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return ReactDOM.createPortal(formContent, document.body);
}

export default observer(GFApplicationForm);
